import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "资讯", to: "/news" },
  { label: "视频", to: "/video" },
  { label: "游戏", to: "/game" },
  { label: "社区", to: "/forum" },
  { label: "关于", to: "/about" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword("");
    setMobileOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? "border-background-200 bg-background-50/95 backdrop-blur"
          : "border-transparent bg-background-50"
      }`}
    >
      <div className="mx-auto flex h-16 w-full items-center gap-3 px-4 md:h-[68px] md:px-6">
        {/* Brand */}
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-fire-fill text-lg" aria-hidden="true"></i>
          </span>
          <span className="font-brand text-xl leading-none text-foreground-950 md:text-2xl">
            天天干天天干
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="ml-4 hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive(item.to)
                  ? "bg-primary-100 text-primary-800"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {/* Desktop search */}
          <form
            onSubmit={handleSearch}
            className="hidden items-center rounded-full border border-background-300 bg-background-100 pl-3 pr-1 md:flex"
          >
            <i className="ri-search-line text-base text-foreground-400" aria-hidden="true"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索资讯、视频、攻略"
              aria-label="站内搜索"
              className="h-9 w-40 bg-transparent px-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 lg:w-52"
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-full bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>

          {/* Mobile search trigger */}
          <Link
            to="/search"
            aria-label="搜索"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-300 text-foreground-700 md:hidden"
          >
            <i className="ri-search-line text-lg" aria-hidden="true"></i>
          </Link>

          {/* Hamburger */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="菜单"
            aria-expanded={mobileOpen}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-300 text-foreground-700 lg:hidden"
          >
            <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {/* Mobile panel */}
      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-4 pt-3 lg:hidden">
          <form
            onSubmit={handleSearch}
            className="mb-3 flex items-center rounded-full border border-background-300 bg-background-100 pl-3 pr-1"
          >
            <i className="ri-search-line text-base text-foreground-400" aria-hidden="true"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索资讯、视频、攻略"
              aria-label="站内搜索"
              className="h-10 flex-1 bg-transparent px-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-xs font-medium text-background-50"
            >
              搜索
            </button>
          </form>
          <nav className="grid grid-cols-2 gap-2">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-md px-4 py-3 text-sm font-medium ${
                  isActive(item.to)
                    ? "bg-primary-100 text-primary-800"
                    : "bg-background-100 text-foreground-700"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}